// RigSentinel · /download — permanent, public install landing page.
//
// Intended as the fixed destination for advertising, social posts and QR
// codes (production: https://getrigsentinel.com/download). Public route,
// outside <Layout/> — no app header/bottom-nav, matching Splash.jsx.
//
// Reuses the SAME usePwaInstall singleton and IosInstallSteps component as
// Splash and Settings — no separate installation logic lives here.

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download as DownloadIcon, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { usePwaInstall } from "@/hooks/usePwaInstall";
import { IosInstallSteps } from "@/components/IosInstallSteps";

export default function Download() {
  const navigate = useNavigate();
  const { installed, canPromptInstall, showIOSInstructions, promptInstall } = usePwaInstall();
  const [installTapped, setInstallTapped] = useState(false);

  const onInstallClick = async () => {
    if (canPromptInstall) {
      const outcome = await promptInstall();
      if (outcome === "accepted") toast.success("RigSentinel installed");
      return;
    }
    // iOS has no programmatic prompt — reveal the Add-to-Home-Screen steps
    // below instead of doing nothing on tap.
    setInstallTapped(true);
  };

  return (
    <div
      data-testid="download-screen"
      className="min-h-screen relative overflow-x-hidden flex flex-col items-center justify-center gap-6 text-white px-5 py-10"
      style={{ minHeight: "100dvh", background: "#0A0B0E" }}
    >
      <div
        className="absolute inset-0 -z-10"
        style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(255,179,0,0.08) 0%, transparent 55%)" }}
      />

      <div className="w-full max-w-xs text-center">
        <div
          data-testid="download-eyebrow"
          className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[var(--rs-text-3)] mb-6"
        >
          <span className="block w-6 h-px bg-[var(--rs-amber)]" />
          RIGSENTINEL
          <span className="block w-6 h-px bg-[var(--rs-amber)]" />
        </div>

        <img
          data-testid="download-logo"
          src="/rigsentinel-logo.png"
          alt="RigSentinel"
          className="block mx-auto mb-6 h-auto w-3/4"
          decoding="async"
        />

        <h1 data-testid="download-headline" className="text-lg font-semibold leading-snug">
          The well at your fingertips.
        </h1>
        <p data-testid="download-description" className="text-sm text-[var(--rs-text-2)] mt-3 leading-relaxed">
          Install RigSentinel on your phone, tablet or computer for faster access to your drilling tools.
        </p>
      </div>

      <div className="w-full max-w-xs flex flex-col gap-3 mt-2">
        {installed ? (
          <button
            data-testid="download-open-btn"
            onClick={() => navigate("/home")}
            className="btn-amber w-full h-12 rounded-md label-up text-xs inline-flex items-center justify-center gap-2"
          >
            OPEN RIGSENTINEL <ArrowRight className="w-4 h-4" />
          </button>
        ) : canPromptInstall || showIOSInstructions ? (
          <>
            <button
              data-testid="download-install-btn"
              onClick={onInstallClick}
              className="btn-amber w-full h-12 rounded-md label-up text-xs inline-flex items-center justify-center gap-2"
            >
              <DownloadIcon className="w-4 h-4" /> INSTALL RIGSENTINEL
            </button>
            {showIOSInstructions && installTapped && (
              <div data-testid="download-ios-instructions" className="card-rs p-3.5 text-left">
                <div className="label-up text-[10px] text-[var(--rs-amber)] mb-2">Install on iPhone/iPad</div>
                <IosInstallSteps testId="download-ios-steps" />
              </div>
            )}
          </>
        ) : (
          <p data-testid="download-install-unavailable" className="text-[11px] text-[var(--rs-text-3)] text-center">
            Installation isn&apos;t available from this browser right now — check your browser menu for an
            &quot;Install app&quot; option, or open this page in Chrome/Safari.
          </p>
        )}
      </div>
    </div>
  );
}
